
import Select from "react-select";
import PrimaryButton from "../../CommonComponents/PrimaryButton";
import { useTheme } from "../../CommonComponents/ThemeProvider";
import tinycolor from "tinycolor2";

const AddPreReleaseForm = ({
    products,
    productOptions,
    selectedProduct,
    setSelectedProduct,
    editProduct,
    setEditProduct,
    editIndex,
    editQty,
    setEditQty,
    addQty,
    setAddQty,
    addItem,
    cancelEdit
}) => {
    const { currentTheme } = useTheme();
    const primaryColor = currentTheme?.color || '#14532d';
    const hoverColor = tinycolor(primaryColor).lighten(8).toString();
    const activeColor = tinycolor(primaryColor).darken(8).toString();
    const selectedProductId = editIndex !== null ? editProduct?.value : selectedProduct?.value;
    // No normalization needed; backend returns full URL
    const selectedProd = products.find(p => p.id === selectedProductId);

    return (
        <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden flex flex-col p-3 md:p-5 min-h-[500px]">
            <h3 className="font-semibold text-xs md:text-sm mb-2 text-brand-green tracking-tight uppercase letter-spacing-wide">1. Add Pre-Release</h3>
            <div className="flex flex-col gap-4 flex-1 text-base">
                {/* Product selection and quantity */}
                <div>
                    <label className="block mb-1 text-sm font-semibold text-gray-700" htmlFor="product-select">Product</label>
                    <div className="flex flex-col gap-2">
                        <div className="flex-1 min-w-[120px]">
                            <Select
                                inputId="product-select"
                                options={productOptions}
                                value={editIndex !== null ? editProduct : selectedProduct}
                                onChange={editIndex !== null ? setEditProduct : setSelectedProduct}
                                placeholder="Search product..."
                                isSearchable
                                isOptionDisabled={option => option.isDisabled}
                                formatOptionLabel={option => {
                                    const prod = products.find(p => p.id === option.value);
                                    // Show warehouse stock for pre-release requests
                                    if (prod) {
                                        console.log(`[AddPreReleaseForm] Product ${prod.name}:`, {
                                            id: prod.id,
                                            name: prod.name,
                                            image: prod.image,
                                            hasImage: !!prod.image,
                                            warehouse_stock: prod.warehouse_stock
                                        });
                                    } else {
                                        console.warn(`[AddPreReleaseForm] Product not found in products array for option:`, option);
                                    }
                                    return prod ? (
                                        <div className="flex items-center gap-2">
                                            {prod.image && (
                                                <img
                                                    src={prod.image}
                                                    alt={prod.name}
                                                    className="w-6 h-6 object-cover rounded"
                                                    onError={e => {
                                                        e.target.onerror = null;
                                                        e.target.src = "/default-product.png";
                                                    }}
                                                />
                                            )}
                                            <span className="font-medium text-sm">{prod.name}</span>
                                            <span className={`text-xs font-semibold ${prod.warehouse_stock === 0 ? 'text-red-500' : 'text-gray-500'}`}>{`(Stock: ${prod.warehouse_stock ?? 0})`}</span>
                                            {prod.warehouse_stock === 0 && (
                                                <span className="ml-2 px-2 py-0.5 rounded bg-red-100 text-red-700 text-xs">Out of stock</span>
                                            )}
                                        </div>
                                    ) : null;
                                }}
                                aria-label="Product selection"
                                styles={{
                                    control: (base) => ({ ...base, minHeight: '36px', height: '36px', fontSize: '15px', borderRadius: 8 }),
                                    valueContainer: (base) => ({ ...base, height: '36px', padding: '0 10px' }),
                                    input: (base) => ({ ...base, margin: '0px', fontSize: '15px' }),
                                    indicatorsContainer: (base) => ({ ...base, height: '36px' }),
                                }}
                            />
                        </div>
                        {/* Add button below search */}
                        {editIndex !== null ? (
                            <div className="flex gap-2 items-center mt-2">
                                <input
                                    type="number"
                                    min={1}
                                    max={editProduct ? (products.find(p => p.id === editProduct.value)?.warehouse_stock || 99) : 99}
                                    value={editQty === 0 ? '' : editQty}
                                    onChange={e => {
                                        const val = e.target.value;
                                        if (val === '' || /^[0-9]+$/.test(val)) {
                                            setEditQty(val === '' ? 0 : Number(val));
                                        }
                                    }}
                                    className="w-20 border rounded-lg px-3 py-2 text-base focus:outline-none"
                                    style={{
                                        borderColor: '#cbd5e1',
                                        outline: 'none',
                                        boxShadow: `0 0 0 2px ${primaryColor}33`,
                                    }}
                                    onFocus={e => e.target.style.boxShadow = `0 0 0 2px ${primaryColor}`}
                                    onBlur={e => e.target.style.boxShadow = `0 0 0 2px ${primaryColor}33`}
                                    aria-label="Quantity"
                                />
                                <PrimaryButton
                                    type="button"
                                    onClick={addItem}
                                    disabled={!editProduct}
                                    className="px-3 py-2 text-base rounded-lg"
                                    style={{ background: primaryColor, color: '#fff' }}
                                    onMouseOver={e => e.currentTarget.style.background = hoverColor}
                                    onMouseOut={e => e.currentTarget.style.background = primaryColor}
                                    onMouseDown={e => e.currentTarget.style.background = activeColor}
                                    onMouseUp={e => e.currentTarget.style.background = hoverColor}
                                >Save</PrimaryButton>
                                <PrimaryButton
                                    type="button"
                                    onClick={cancelEdit}
                                    className="px-3 py-2 text-base rounded-lg bg-gray-200 text-gray-700 border border-gray-300"
                                    style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}
                                >Cancel</PrimaryButton>
                            </div>
                        ) : (
                            <div className="flex gap-2 items-center mt-2 justify-end">
                                <input
                                    type="number"
                                    min={1}
                                    max={selectedProduct ? (products.find(p => p.id === selectedProduct.value)?.warehouse_stock || 99) : 99}
                                    value={addQty === 0 ? '' : addQty}
                                    onChange={e => {
                                        const val = e.target.value;
                                        if (val === '' || /^[0-9]+$/.test(val)) {
                                            setAddQty(val === '' ? 0 : Number(val));
                                        }
                                    }}
                                    className="w-20 border rounded-lg px-3 py-2 text-base focus:outline-none"
                                    style={{
                                        borderColor: '#cbd5e1',
                                        outline: 'none',
                                        boxShadow: `0 0 0 2px ${primaryColor}33`,
                                    }}
                                    onFocus={e => e.target.style.boxShadow = `0 0 0 2px ${primaryColor}`}
                                    onBlur={e => e.target.style.boxShadow = `0 0 0 2px ${primaryColor}33`}
                                    aria-label="Quantity"
                                />
                                <PrimaryButton
                                    type="button"
                                    onClick={addItem}
                                    disabled={
                                        !selectedProduct ||
                                        addQty < 1 ||
                                        (selectedProduct && (products.find(p => p.id === selectedProduct.value)?.warehouse_stock === 0))
                                    }
                                    className="px-3 py-2 text-base rounded-lg"
                                    style={{ background: primaryColor, color: '#fff' }}
                                    onMouseOver={e => e.currentTarget.style.background = hoverColor}
                                    onMouseOut={e => e.currentTarget.style.background = primaryColor}
                                    onMouseDown={e => e.currentTarget.style.background = activeColor}
                                    onMouseUp={e => e.currentTarget.style.background = hoverColor}
                                >Add</PrimaryButton>
                            </div>
                        )}
                    </div>
                    {/* Product description */}
                    {(editIndex !== null ? editProduct : selectedProduct) && selectedProd?.description && (
                        <div className="text-xs text-gray-500 mt-1 mb-1">
                            {selectedProd.description}
                        </div>
                    )}
                    {(editIndex !== null ? editProduct : selectedProduct) && selectedProd?.image && (
                        <div className="mt-2 w-full max-h-48 flex items-center justify-center overflow-hidden">
                            <img
                                src={selectedProd.image}
                                alt={selectedProd.name}
                                className="w-full max-h-48 object-contain rounded-lg border border-gray-200"
                                onError={e => {
                                    e.target.onerror = null;
                                    e.target.src = "/default-product.png";
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddPreReleaseForm;
